import { GenericTable } from "@shared/components/GenericTable";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import React, { useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { useDayEndReconciliations } from "../../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";

export const DayEndReconciliationList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page") || 1),
  );
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();

  const filters = useMemo(
    () => ({
      remoteUtilityId: Number(remoteUtilityId),
      page: currentPage,
      limit: PAGE_SIZE,
      search: searchParams.get("search") || "",
    }),
    [remoteUtilityId, currentPage, searchParams],
  );

  const { data, isLoading } = useDayEndReconciliations(filters);

  const tableData = data?.results || [];
  const totalCount = data?.count || 0;

  const handleSearch = (searchTerm: string) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "periodFrom",
      header: "Date",
      type: "custom",
      render: (value: any, row: any) => (
        <span className="text-gray-900 font-medium">
          {value} - {row.periodTo}
        </span>
      ),
    },
    {
      key: "sourceBankDisplay",
      header: "Bank Account",
      type: "custom",
      render: (value: any) => (
        <span className="text-gray-900 font-medium">{value}</span>
      ),
    },
    {
      key: "systemTotal",
      header: `System Total (${currency})`,
      type: "custom",
      render: (value: any) => (
        <span className="font-semibold text-gray-900">{value}</span>
      ),
    },
    {
      key: "bankTotal",
      header: `Bank Total (${currency})`,
      type: "custom",
      render: (value: any) => (
        <span className="font-semibold text-gray-900">{value}</span>
      ),
    },
    {
      key: "variance",
      header: `Variance (${currency})`,
      type: "custom",
      render: (value: any) => {
        if (value === 0) {
          return <span className="text-gray-500">0.00</span>;
        }
        const isPositive = value > 0;
        const displayValue = `${Math.abs(value).toFixed(2)}`;
        const textValue = isPositive ? `+${displayValue}` : `-${displayValue}`;
        return <span className="text-red-600 font-medium">{textValue}</span>;
      },
    },
    {
      key: "createdOn",
      header: "Created On",
      type: "custom",
      render: (value: any) => (
        <span className="text-gray-500 text-sm">{value}</span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "custom",
      render: (value: any) => {
        let classes =
          "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100";

        if (!value) return null;

        const lowerValue = value.toLowerCase();

        if (lowerValue === "not started") {
          classes = "bg-red-50 text-red-700 border-red-200 hover:bg-red-100";
        } else if (lowerValue === "in progress") {
          classes =
            "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100";
        } else if (lowerValue === "signed off" || lowerValue === "matched") {
          classes =
            "bg-green-50 text-green-700 border-green-200 hover:bg-green-100";
        }

        return (
          <Badge variant="outline" className={classes}>
            {value}
          </Badge>
        );
      },
    },
  ];

  const pagination = {
    currentPage,
    totalPages: Math.ceil(totalCount / PAGE_SIZE) || 1,
    totalItems: totalCount,
    itemsPerPage: PAGE_SIZE,
    startItem: (currentPage - 1) * PAGE_SIZE + 1,
    endItem: Math.min(currentPage * PAGE_SIZE, totalCount),
  };

  const actions: TableAction<any>[] = useMemo(
    () => [
      {
        label: "View details",
        onClick: (row: any) =>
          navigate(`/cx/payments/day-end-reconcilation/view/${row.id}`),
      },
    ],
    [navigate],
  );

  return (
    <div>
      <GenericTable
        data={tableData}
        columns={columns as any}
        actions={actions}
        rowKey="id"
        searchPlaceholder="Search reconciliations..."
        searchParamName="search"
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No reconciliations yet."
      />
    </div>
  );
};

export default DayEndReconciliationList;
